/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import type { DealerContextServiceV1, UnsubscribeFn } from '@oneaudi/dealer-context-service';
import {
  ContentServiceV1 as ContentService,
  ContentChangeListener,
} from '@volkswagen-onehub/audi-content-service';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { createTrackingService } from '@volkswagen-onehub/oneaudi-os-adapter-tracking';
import i18n from '../i18n';
import { GRAPHQL_GATEWAY_URL, IS_LOCAL_ENV } from './constants/environment';
import GlobalStyles from './styles';
import { COLORS } from './styles/variables';
import VehicleResultsContainer from './components/VehicleResultsContainer';
import { FilteredWormwoodVehiclesQuery } from './types';
import { AppContent, mapContent } from './ContentMapping/contentMapping';
import ErrorBoundary from './components/ErrorBoundary';
import useLocationSearchData from './hooks/Fetch/useLocationSearchData';
import { setLocation } from './redux/locationReducer';
import { setConfig, setMarket, selectAppConfig, listOfDealersType } from './redux/appConfigReducer';
import getMarket from './utils/getMarket';
import { setFiltersFromLS, setMainDealerId, selectFilters } from './redux/filtersReducer';
import { setVendorCTAs } from './redux/vendorCTAsReducer';
import { getEnabledVendorCTAs } from './utils/getEnabledVendorCTAs';
import { setPriceBreakdowns } from './redux/priceBreakdownReducer';
import {
  filterInitialState,
  resultsReturnedFailed,
  tier3ListOfDealers,
} from './utils/analyticsEventListeners';

import mockContent from './sample-vendorCTA-content.json';
import mockPriceBreakdown from './sample-priceBreakdownContent.json';
import { TIER_3_CONFIG } from './constants/mockVehicleForTesting';
import { setInitialSortBy } from './redux/sortByReducer';
import useContentProcessor from './hooks/useContentProcessor';
import useFilterProcessor from './hooks/Filters/useFilterProcessor';

interface FeatureAppProps {
  configProvider?: GfaServiceConfigProviderV1;
  trackingService?: TrackingServiceV2;
  dealerContextService?: DealerContextServiceV1;
  remoteConfig: any;
  contentService?: ContentService;
}

const FeatureApp: React.FC<FeatureAppProps> = ({
  // @ts-ignore
  trackingService = createTrackingService(__FEATURE_APP_NAME__),
  remoteConfig,
  contentService,
}: FeatureAppProps) => {
  const configFile = remoteConfig;

  // ----------------------------------------------------------------------------------------------
  // Get location from LS and save to Redux
  useLocationSearchData();

  // Set config, filters and sortBy
  useFilterProcessor(configFile);

  // CONTENT Hook. Grabbing all content data, and processing it inside this hook, saving data to redux
  useContentProcessor(contentService);

  // ----------------------------------------------------------------------------------------------

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    trackingService.featureAppName = __FEATURE_APP_NAME__;
    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
        },
      },
    };
    trackingService.track(readyEvent);
  }, [trackingService]);

  return (
    <I18nextProvider i18n={i18n}>
      {/* @ts-ignore:next-line */}
      <ErrorBoundary>
        <ThemeProvider theme={COLORS}>
          <div className="resetWrapper">
            <VehicleResultsContainer />
          </div>
          <GlobalStyles />
        </ThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
